<script>
  import { createEventDispatcher, onDestroy, onMount, tick } from "svelte";

  export let departments = [];
  export let selector = false;
  export let selectedDepartments = [];
  export let items = departments;
  export let selectedItems = selectedDepartments;
  export let searchPlaceholder = "Search departments";

  let open = false;
  let search = "";
  let popoverRoot;
  let trigger;
  let searchInput;
  const dispatch = createEventDispatcher();

  $: availableItems = selector ? departments : items.length ? items : departments;
  $: availableSelectedItems = selector ? selectedDepartments : selectedItems.length ? selectedItems : selectedDepartments;
  $: filteredDepartments = availableItems.filter((department) =>
    department.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()),
  );

  async function toggleMenu() {
    open = !open;
    if (selector && open) {
      search = "";
      await tick();
      searchInput?.focus();
    }
  }

  function closeSelector(event) {
    if (selector && open && event.key === "Escape" && popoverRoot?.contains(event.target)) {
      event.preventDefault();
      open = false;
      trigger?.focus();
    }
  }

  function toggleDepartment(department) {
    dispatch("toggle", department);
    if (selector) {
      open = false;
      trigger?.focus();
    }
  }

  function handleOutsideClick(event) {
    if (open && popoverRoot && !popoverRoot.contains(event.target)) {
      open = false;
    }
  }

  onMount(() => {
    window.addEventListener("click", handleOutsideClick);
  });

  onDestroy(() => {
    window.removeEventListener("click", handleOutsideClick);
  });
</script>

<svelte:window on:keydown={closeSelector} />

<div class="pax-ai-departments" class:selector bind:this={popoverRoot}>
  <button
    type="button"
    class="more-button"
    bind:this={trigger}
    aria-expanded={open}
    aria-haspopup={selector ? "dialog" : undefined}
    on:click={toggleMenu}
  >
    {#if selector}
      <span>{availableSelectedItems[0] || "Select department"}</span>
      <span aria-hidden="true">▾</span>
    {:else}
      More …
    {/if}
  </button>

  {#if open}
    <div class="departments-popover" role={selector ? "dialog" : undefined} aria-label={selector ? "Select department" : undefined}>
      <input
        type="search"
        bind:this={searchInput}
        bind:value={search}
        placeholder={searchPlaceholder}
        aria-label={searchPlaceholder}
      />
      <div class="department-options">
        {#each filteredDepartments as department}
          <button
            type="button"
            class:selected={availableSelectedItems.includes(department)}
            aria-pressed={availableSelectedItems.includes(department)}
            on:click={() => toggleDepartment(department)}
          >
            {department}
          </button>
        {:else}
          <p class="no-results">No matching departments.</p>
        {/each}
      </div>
    </div>
  {/if}
</div>

<style>
  .pax-ai-departments {
    position: relative;
  }

  .pax-ai-departments.selector {
    width: min(100%, 28rem);
  }

  .selector .more-button {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    gap: .75rem;
    padding: .75rem 1rem;
    border-radius: .5rem;
    font-size: .9rem;
    font-weight: 400;
    text-align: left;
  }

  .selector .more-button span:first-child {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .selector .departments-popover {
    left: 0;
    right: auto;
    width: 100%;
    box-sizing: border-box;
    z-index: 5;
  }

  .selector input:focus-visible {
    outline: 2px solid var(--pax-white-new);
    outline-offset: 2px;
  }

  .no-results {
    margin: .5rem 0;
    color: var(--pax-text-secondary-new);
    font-size: .875rem;
  }

  .more-button {
    padding: .5rem .8rem;
    border: 1px solid var(--pax-border-new);
    border-radius: 999px;
    background: var(--pax-surface-control-new);
    color: var(--pax-white-new);
    font: inherit;
    font-size: .8rem;
    font-weight: 600;
    cursor: pointer;
  }

  .more-button:hover {
    background: var(--pax-surface-control-hover-new);
    border-color: var(--pax-white-new);
  }

  .departments-popover {
    position: absolute;
    z-index: 2;
    top: calc(100% + .5rem);
    right: 0;
    width: 18rem;
    padding: .75rem;
    border: 1px solid var(--pax-border-strong-new);
    border-radius: .75rem;
    background: var(--pax-surface-raised-new);
  }

  input {
    width: 100%;
    margin-bottom: .5rem;
    padding: .5rem .65rem;
    box-sizing: border-box;
    border: 1px solid var(--pax-border-strong-new);
    border-radius: .5rem;
    outline: 0;
    background: var(--pax-surface-control-new);
    color: var(--pax-white-new);
    font: inherit;
    font-size: .8rem;
  }

  input::placeholder {
    color: var(--pax-text-secondary-new);
  }

  input[type="search"]::-webkit-search-cancel-button {
    width: 1.25rem;
    height: 1.25rem;
    margin-left: .25rem;
    border-radius: 100%;
    background: var(--pax-white-new)
      url("/img/assistant/icons/close_dark.svg") center / .6rem .6rem no-repeat;
    cursor: pointer;
    -webkit-appearance: none;
  }

  .department-options {
    display: flex;
    max-height: 14rem;
    flex-direction: column;
    gap: .25rem;
    overflow-y: auto;
    scrollbar-color: var(--pax-white-new) transparent;
    scrollbar-width: thin;
  }

  .department-options::-webkit-scrollbar {
    width: .5rem;
  }

  .department-options::-webkit-scrollbar-track {
    background: transparent;
  }

  .department-options::-webkit-scrollbar-thumb {
    border-radius: 999px;
    background: var(--pax-white-new);
  }

  .department-options button {
    width: 100%;
    padding: .5rem .65rem;
    border: 0;
    border-radius: .5rem;
    background: transparent;
    color: var(--pax-white-new);
    font: inherit;
    font-size: .8rem;
    text-align: left;
    cursor: pointer;
  }

  .department-options button:hover {
    background: var(--pax-surface-control-hover-new);
  }

  .department-options button.selected {
    background: var(--pax-white-new);
    color: var(--pax-black-new);
  }
</style>
